import { Stack, Popover, Typography, IconButton, useTheme, Button, Checkbox } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { deleteLabel, getLabels, toggleLabel } from '../slice';
import { FormProvider, Iconify, Scrollbar, SearchBar } from '@app/components';
import { Strings } from '@app/constants';
import { CreateLabelPopup } from './edit-label-popup';
import { ILabel } from '../types';
import { selectCardDetailState, selectGetLabelsState } from '../selector';
import { useRequestState } from '@app/hooks';

interface Props {
  openLabelPopup: HTMLElement | null;
  handleCloseLabelPopup: () => void;
}
const AddLabelPopup = ({ openLabelPopup, handleCloseLabelPopup }: Props) => {
  const open = Boolean(openLabelPopup);
  const theme = useTheme();
  const id = open ? 'simple-popover' : undefined;
  const methods = useForm<any>();
  const dispatch = useDispatch();

  const [searchQuery, setSearchQuery] = useState('');
  const [openCreateLabelPopup, setOpenCreateLabelPopup] = useState(false);
  const [selectedLabels, setSelectedLabels] = useState<ILabel[]>([]);

  const { data: cardDetails } = useRequestState({
    stateSelector: selectCardDetailState,
    errorShown: true,
  });
  const { data: labelList } = useRequestState({
    stateSelector: selectGetLabelsState,
    errorShown: true,
  });

  const filteredLabels = labelList?.filter((label: any) =>
    label?.name?.toLowerCase().includes(searchQuery)
  );

  useEffect(() => {
    if (cardDetails?.id) {
      dispatch(getLabels({ boardId: cardDetails?.boardId }));
    }
  }, [dispatch, cardDetails]);

  useEffect(() => {
    if (labelList && cardDetails?.addedLabels) {
      const filteredLabels = labelList.filter((label: ILabel) =>
        cardDetails.addedLabels.some((cardLabel: ILabel) => cardLabel.id === label.id)
      );
      setSelectedLabels(filteredLabels);
    }
  }, [labelList, cardDetails?.labels]);

  const handleLabelOnChange = (label: ILabel) => {
    console.log('handleLabelOnChange');
    if (label.id) {
      dispatch(toggleLabel({ labelId: label.id, cardId: cardDetails?.id }));
    }
    const isSelected = selectedLabels.some((selectedLabel) => selectedLabel.id === label.id);
    if (isSelected) {
      setSelectedLabels((prev) => prev.filter((item) => item.id !== label.id));
    } else {
      setSelectedLabels((prev) => [...prev, label]);
    }
  };
  return (
    <>
      <FormProvider methods={methods}>
        <Stack sx={{ width: '30%' }} mt={4} ml={0}>
          <Popover
            id={id}
            open={open}
            onClose={handleCloseLabelPopup}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            sx={{
              '& .MuiPaper-root': {
                width: '30%',
                height: 'auto',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                borderRadius: '10px',
                boxShadow: theme.shadows[5],
              },
            }}
          >
            <Scrollbar>
              <Stack sx={{ width: '100%' }} p={2}>
                <Stack
                  // p={2}
                  display={'flex'}
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems={'center'}
                >
                  <Typography variant="h6" sx={{ textAlign: 'center' }}>
                    {Strings.taskBoard.labels}
                  </Typography>
                  <IconButton
                    aria-label="close"
                    sx={{
                      color: theme.palette.common.black,
                    }}
                    onClick={handleCloseLabelPopup}
                  >
                    <Iconify icon={'mi:close'} />
                  </IconButton>
                </Stack>
                <SearchBar
                  onChange={(e) => {
                    setSearchQuery(e.target.value.toLowerCase());
                  }}
                  size="small"
                  placeholder="search"
                />
                <Stack
                  flexDirection={'row'}
                  mt={2}
                  mb={1}
                  justifyContent={'space-between'}
                  alignItems={'center'}
                >
                  <Typography>{Strings.taskBoard.labels}</Typography>
                  <Button
                    sx={{
                      backgroundColor: theme.palette.taskboard.raisinBlack,
                      color: 'white',
                      '&:hover': {
                        bgcolor: theme.palette.taskboard.raisinBlack,
                        color: theme.palette.error.contrastText,
                      },
                    }}
                    onClick={() => {
                      setOpenCreateLabelPopup(true);
                    }}
                  >
                    {Strings.button.addNew}
                  </Button>
                </Stack>

                {filteredLabels?.length > 0 ? (
                  filteredLabels?.map((label: any, index: any) => {
                    return (
                      <Stack mt={0.5} key={label.id}>
                        <Stack
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Checkbox
                            name={'labelColor'}
                            sx={{
                              '&.Mui-checked': {
                                color: theme.palette.taskboard.raisinBlack,
                              },
                            }}
                            checked={selectedLabels.some(
                              (selectedLabel) => selectedLabel.id === label.id
                            )}
                            onChange={() => {
                              handleLabelOnChange(label);
                            }}
                            disabled={!label?.name}
                          />
                          <Stack
                            sx={{
                              height: '32px',
                              width: '70%',
                              borderRadius: '4px',
                              backgroundColor: label.color || theme.palette.primary.main,
                              justifyContent: 'center',
                            }}
                            pl={2}
                          >
                            {label.name ?? ''}
                          </Stack>
                          <Stack onClick={() => dispatch(deleteLabel({ labelId: label.id }))}>
                            <IconButton
                              aria-label="close"
                              sx={{
                                color: theme.palette.common.black,
                              }}
                            >
                              <Iconify icon={'mi:delete'} />
                            </IconButton>
                          </Stack>
                        </Stack>
                      </Stack>
                    );
                  })
                ) : (
                  <Typography>{Strings.taskBoard.noLabelsFound}</Typography>
                )}
              </Stack>
              <CreateLabelPopup
                openCreateLabelPopup={openCreateLabelPopup}
                handleCloseCreateLabelPopup={() => {
                  setOpenCreateLabelPopup(false);
                }}
                boardId={cardDetails?.boardId}
              />
            </Scrollbar>
          </Popover>
        </Stack>
      </FormProvider>
    </>
  );
};
export { AddLabelPopup };
